import { inject, Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { Specimen } from 'fhir/r4';
import { TableFilteringService, TableFilters, TableRowModel } from '../shared/table-filtering.service';
import { backendEndPointToken } from '../app.config';
import { LoadingUIEnabled } from '../loading.interceptor';

export type SpecimenRow = Specimen & TableRowModel;

@Injectable({
  providedIn: 'root'
})
export class SpecimenSearchService {
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly tableFiltering = inject(TableFilteringService) as unknown as TableFilteringService<Specimen, SpecimenRow>;
  private readonly silentHttpContext = new HttpContext().set(LoadingUIEnabled, false);

  readonly dataSource = new MatTableDataSource<SpecimenRow>();
  readonly loading$ = this.tableFiltering.loading$;
  readonly total$ = this.tableFiltering.total$;
  readonly error$ = this.tableFiltering.error$;

  init(patientId?: string | null): void {
    const defaultQuery = patientId 
      ? `/Specimen?subject=Patient/${patientId}`
      : `/Specimen`;

    this.tableFiltering.init({
      resourceType: 'Specimen',
      defaultQuery,
      dataSource: this.dataSource,
      pageSize: 25,
      baseUrl: this.backendUrl,
      transformEntry: (resource: Specimen) => this.transform(resource),
      httpOptions: { context: this.silentHttpContext },
      enableLocalFiltering: false
    });
  }

  destroy(): void {
    this.tableFiltering.destroy();
    this.dataSource.disconnect();
  }

  search(filters: TableFilters): void {
    this.tableFiltering.search(filters);
  }

  changePage(pageIndex: number): void {
    this.tableFiltering.changePage(pageIndex);
  }

  refresh(): void {
    this.tableFiltering.refreshCurrentPage();
  }

  private transform(resource: Specimen): SpecimenRow {
    return {
      ...resource,
      id: resource.id ?? '',
      resourceType: 'Specimen',
      rawResource: resource
    };
  }
}
